import { memo } from 'react'

import { BoxProps } from '@chakra-ui/react'

import { Offer } from '@wwt/entities/search-response'

import { ImageSlider } from '@/features/Image'
import { cn } from '@/shared/lib/cn'
import { LinkBox, LinkOverlay } from '@/shared/ui/link/index.ts'

import { useOfferCard } from '../model/useOfferCard.ts'
import { useOfferCardDefault } from '../model/useOfferCardDefault.ts'
import { OfferCardFooter } from './OfferCardElements/OfferCardFooter.tsx'
import { OfferCardHighRatingTag } from './OfferCardElements/OfferCardHighRatingTag.tsx'
import { OfferCardHotelNameWithRating } from './OfferCardElements/OfferCardHotelNameWithRating.tsx'
import { OfferCardLocation } from './OfferCardElements/OfferCardLocation.tsx'
import { OfferCardTitle } from './OfferCardElements/OfferCardTitle.tsx'

interface OfferCardMobileMapProps extends BoxProps {
	isActive?: boolean
	onLocationClick?: (id: string) => void
	offer: Offer
	hasClickableLocation?: boolean
	className?: string
	onNavigation?: () => void
}

export const OfferCardMobileMap = memo(
	({
		isActive,
		onLocationClick,
		offer,
		hasClickableLocation,
		className,
		onNavigation
	}: OfferCardMobileMapProps) => {
		const {
			onOfferCardLocationClick,
			onLinkClick,
			isLocationDisabled,
			images
		} = useOfferCard(offer, onLocationClick)
		const roomOfferPath = useOfferCardDefault(offer)

		return (
			<LinkBox
				className={cn(
					'relative flex h-[16.625rem] w-[15.5rem] flex-col rounded-md bg-white hover:shadow-md',
					isActive && 'border-brand-200 border border-solid shadow-md',
					className
				)}
				data-wwt-id="offer-card--unique"
				onClick={onLinkClick}
			>
				<ImageSlider
					classNames={{
						base: 'h-[8.5rem] min-h-[8.5rem] w-full overflow-hidden rounded-md'
					}}
					images={images}
					to={roomOfferPath}
					onImageClick={onNavigation}
				/>

				<div className="flex flex-1 flex-col p-2">
					<div className="flex max-h-[6.375rem] flex-1 flex-col justify-evenly gap-1">
						<OfferCardHighRatingTag />

						<div className="flex w-fit flex-col gap-1 text-gray-400">
							<LinkOverlay
								className="ts-7 hover:text-brand-200 inline-block h-full leading-[1.059rem]"
								data-wwt-id="offer-card__title-link--unique"
								to={roomOfferPath}
								onClick={onNavigation}
							>
								<OfferCardTitle
									className="gap-0"
									roomNameClassName="line-clamp-1"
									offer={offer}
								/>
							</LinkOverlay>
						</div>

						<OfferCardHotelNameWithRating
							className="ts-7"
							hotelNameClassName="line-clamp-1"
							offer={offer}
							ratingStarClassName="size-[1rem]"
						/>

						<OfferCardLocation
							className="line-clamp-1"
							offer={offer}
							isDisabled={isLocationDisabled}
							hasClickableLocation={hasClickableLocation}
							onClick={onOfferCardLocationClick}
							clickableLocationProps={{
								className: 'line-clamp-1',
								w: 'fit-content',
								textStyle: 'body-text-8',
								noOfLines: 1,
								wordBreak: 'break-word',
								isTrimmed: true
							}}
						/>
					</div>

					<hr className="mt-2 block w-full border-t-0 border-b border-solid opacity-[60%]" />

					<OfferCardFooter
						className="justify-between px-2 pt-2 pb-3"
						likeButtonClassName="h-5! w-5! mr-4!"
						dislikeButtonClassName="h-5! w-5!"
						priceClassName="ml-auto ts-5 line-clamp-1"
						offer={offer}
					/>
				</div>
			</LinkBox>
		)
	}
)

OfferCardMobileMap.displayName = 'OfferCardMobileMap'
